<script lang="ts">
	import ArrowIcon from '$lib/components/marketing/redesign2025/ArrowIcon.svelte';
	import * as Dialog from '$lib/components/ui/dialog';
	import { getLocale, localizeHref } from '@/paraglide/runtime';
	import Star from '@lucide/svelte/icons/star';
	import { resolve } from '$app/paths';
	import { m } from '$lib/paraglide/messages.js';

	let priceSimOpen = $state(false);

	// Subscribe to locale so translated copy updates on /en and /gl routes
	const locale = $derived(getLocale());

	const landingReviews = $derived.by(() => {
		void locale;
		return [
			{
				quote: m.landing_review1_quote(),
				name: 'Nicolas',
				subtitle: m.landing_review1_sub(),
				avatarUrl:
					'https://zarpar.eu/api/files/_pbc_344172009/hcr9b9yr0ta0jrf/screenshot_2026_03_16_at_18_44_54ggoc6tna_te6sgy8oxv.19.webp?thumb=128x128'
			},
			{
				quote: m.landing_review2_quote(),
				name: 'Pedro',
				subtitle: m.landing_review2_sub(),
				avatarUrl:
					'https://zarpar.eu/api/files/_pbc_344172009/akuiq2lee67a6u9/pedrito_ypl9n3yl5k_rr85a3pacp.webp?thumb=128x128'
			},
			{
				quote: m.landing_review3_quote(),
				name: 'Alberto',
				subtitle: m.landing_review3_sub(),
				avatarUrl:
					'https://zarpar.eu/api/files/_pbc_344172009/a8d17tacn2xaax5/screenshot_2026_03_16_at_18_42_cqsjie73be_itsmi7pf8i.45.webp?thumb=128x128'
			},
			{
				quote: m.landing_review4_quote(),
				name: 'María',
				subtitle: m.landing_review4_sub(),
				avatarUrl:
					'https://zarpar.eu/api/files/_pbc_344172009/mq2vvjqvkaoni3s/screenshot_2025_12_18_at_12_41_3hng31ikoh_u6rqg63t82.14.webp?thumb=128x128'
			},
			{
				quote: m.landing_review5_quote(),
				name: 'Gerardo',
				subtitle: m.landing_review5_sub(),
				avatarUrl:
					'https://zarpar.eu/api/files/_pbc_344172009/5i5sv445b2mh6ry/ger_0nbv4j087n_ndmrdoruxw.webp?thumb=128x128'
			},
			{
				quote: m.landing_review6_quote(),
				name: 'Guido',
				subtitle: m.landing_review6_sub(),
				avatarUrl:
					'https://zarpar.eu/api/files/_pbc_344172009/i3c6g45fff12709/screenshot_2026_03_16_at_18_45_bkceqgushf_2c02vpuhy9.40.webp?thumb=128x128'
			}
		];
	});

	const instagramReviews = $derived.by(() => {
		void locale;
		return [
			{
				alt: m.landing_instagram_review_patricia_alt(),
				href: 'https://www.instagram.com/p/DaJDkfMjcV1/'
			},
			{
				alt: m.landing_instagram_review_collage_alt(),
				href: 'https://www.instagram.com/p/DaVIOFAAtw3/?img_index=7'
			},
			{
				alt: m.landing_instagram_review_cies_recommendation_alt(),
				href: 'https://www.instagram.com/carla_dipinto/'
			}
		] as const;
	});

	const steps = $derived.by(() => {
		void locale;
		return [
			{
				num: '01',
				title: m.landing_step1_title(),
				body: m.landing_step1_body()
			},
			{
				num: '02',
				title: m.landing_step2_title(),
				body: m.landing_step2_body()
			},
			{
				num: '03',
				title: m.landing_step3_title(),
				body: m.landing_step3_body()
			},
			{
				num: '04',
				title: m.landing_step4_title(),
				body: m.landing_step4_body()
			}
		];
	});

	const priceIncludes = $derived.by(() => {
		void locale;
		return [
			{
				title: m.landing_price_include1_title(),
				sub: m.landing_price_include1_sub(),
				included: true
			},
			{
				title: m.landing_price_include2_title(),
				sub: m.landing_price_include2_sub(),
				included: true
			},
			{
				title: m.landing_price_include3_title(),
				sub: m.landing_price_include3_sub(),
				included: true
			},
			{
				title: m.landing_price_include4_title(),
				sub: m.landing_price_include4_sub(),
				optional: true
			},
			{
				title: m.landing_price_include5_title(),
				sub: m.landing_price_include5_sub(),
				optionalFree: true
			}
		];
	});

	const trustItems = $derived.by(() => {
		void locale;
		return [
			{
				n: m.landing_trust1_n(),
				p: m.landing_trust1_p()
			},
			{ n: m.landing_trust2_n(), p: m.landing_trust2_p() },
			{ n: m.landing_trust3_n(), p: m.landing_trust3_p() },
			{ n: m.landing_trust4_n(), p: m.landing_trust4_p() }
		];
	});
</script>

<!-- HERO -->
<header class="zp-hero">
	<div class="absolute inset-0 z-0" aria-hidden="true">
		<enhanced:img
			src="/static/img/landing/redesign-2025/photos/calm-misty.avif"
			alt=""
			class="h-full w-full object-cover object-[center_38%]"
			fetchpriority="high"
			sizes="100vw"
		></enhanced:img>
	</div>
	<div class="relative z-2 w-full pb-8 md:pb-[8vh]">
		<div class="mx-auto max-w-[1240px] px-5 md:px-10">
			<a
				href="https://zarpar.eu/ayuda/blog/nueva-ley-aqluiler-embarcaciones/"
				class="group mb-6 inline-flex items-center gap-2.5 text-[0.72rem] font-semibold tracking-[0.22em] text-zp-muted uppercase"
			>
				<span class="zp-pulse-dot" aria-hidden="true"></span>
				<span
					class="border-b border-transparent pb-px transition-colors group-hover:border-zp-muted/70"
				>
					{m.any_tasty_mule_reap()}
				</span>
			</a>
			<h1 class="mb-6 max-w-[14ch] text-[clamp(2.7rem,6.4vw,5.6rem)] leading-none">
				{m.landing_hero_h1_lead()}<em class="text-zp-navy italic">{m.landing_hero_h1_em()}</em>
			</h1>
			<p class="mb-8 max-w-[46ch] text-[clamp(1.05rem,1.5vw,1.32rem)] leading-snug text-zp-prose">
				{m.landing_hero_lead_before()}<strong>{m.landing_hero_lead_list()}</strong
				>{m.landing_hero_lead_after()}
			</p>
			<div class="flex flex-wrap items-center gap-4">
				<a
					href={resolve(localizeHref('/anadir-barco'))}
					class="inline-flex items-center gap-2 rounded-md border border-transparent bg-zp-navy px-6 py-3.5 text-[0.86rem] font-semibold text-zp-paper transition-all hover:-translate-y-px hover:bg-zp-ink"
				>
					{m.landing_cta_register()}
					<ArrowIcon />
				</a>
				<a
					href="#como-funciona"
					class="inline-flex items-center rounded-md border border-zp-line bg-transparent px-6 py-3.5 text-[0.86rem] font-semibold text-zp-ink transition-colors hover:border-zp-ink"
				>
					{m.landing_cta_how()}
				</a>
			</div>
			<div
				class="mt-10 flex max-w-[520px] flex-wrap items-start gap-3.5 border-t border-zp-line pt-6 md:flex-nowrap md:items-center md:gap-3.5"
			>
				<div class="font-serif text-[2.4rem] leading-none text-zp-navy md:text-[2.4rem]">
					15<span class="ml-1 align-baseline font-sans text-[0.9rem] font-semibold text-zp-muted"
						>%</span
					>
				</div>
				<p class="m-0 text-[0.9rem] leading-snug text-zp-muted max-md:text-zp-prose">
					{m.landing_commission_lead()}<b class="font-semibold text-zp-ink"
						>{m.landing_commission_emphasis()}</b
					>
				</p>
			</div>
		</div>
	</div>
</header>

<!-- THE LAW -->
<section class="bg-zp-navy py-20 text-zp-paper md:py-[120px]">
	<div class="mx-auto max-w-[1240px] px-5 md:px-10">
		<div class="grid items-center gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20">
			<div>
				<span class="text-[0.72rem] font-semibold tracking-[0.22em] text-zp-paper/60 uppercase"
					>{m.landing_law_eyebrow()}</span
				>
				<h2 class="mt-5 max-w-[16ch] text-[clamp(2.1rem,4vw,3.6rem)] leading-[1.05] text-zp-paper">
					{m.landing_law_h2_lead()}<em class="text-zp-accent-light italic"
						>{m.landing_law_h2_em()}</em
					>
				</h2>
			</div>
			<div>
				<p class="mb-5 text-[1.08rem] text-zp-paper/78">
					{m.landing_law_p1_before()}<strong>{m.landing_law_p1_list()}</strong
					>{m.landing_law_p1_after()}
				</p>
				<p class="mb-0 text-[1.08rem] text-zp-paper/78">
					{m.landing_law_p2()}<b class="text-zp-paper">{m.landing_law_p2_bold()}</b>
				</p>
				<div class="mt-9 flex gap-8">
					<div class="border-l-2 border-zp-accent pl-[18px]">
						<div class="font-serif text-[2.1rem] leading-none">3</div>
						<span class="mt-1.5 block text-[0.82rem] text-zp-paper/60">
							{m.landing_law_stat3_line1()}<br />{m.landing_law_stat3_line2()}
						</span>
					</div>
					<div class="border-l-2 border-zp-accent pl-[18px]">
						<div class="font-serif text-[2.1rem] leading-none">0</div>
						<span class="mt-1.5 block text-[0.82rem] text-zp-paper/60">
							{m.landing_law_stat0_line1()}<br />{m.landing_law_stat0_line2()}
						</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</section>

<!-- HOW IT WORKS -->
<section id="como-funciona" class="py-20 md:py-[120px]">
	<div class="mx-auto max-w-[1240px] px-5 md:px-10">
		<div class="mb-16 max-w-[780px]">
			<span
				class="mb-5 block text-[0.72rem] font-semibold tracking-[0.22em] text-zp-muted uppercase"
				>{m.landing_how_eyebrow()}</span
			>
			<h2 class="text-[clamp(2rem,3.8vw,3.3rem)] leading-[1.06]">
				{m.landing_how_h2()}
			</h2>
		</div>
		<div
			class="grid overflow-hidden rounded-[18px] border border-zp-line bg-zp-line md:grid-cols-2 lg:grid-cols-4"
		>
			{#each steps as step (step.num)}
				<div class="bg-zp-paper px-8 pt-10 pb-11 md:px-8">
					<div class="mb-6 font-serif text-[1.05rem] tracking-wide text-zp-accent md:mb-11">
						{step.num}
					</div>
					<h3 class="mb-3.5 text-[1.32rem] leading-snug">{step.title}</h3>
					<p class="m-0 text-[0.96rem] text-zp-muted">{step.body}</p>
				</div>
			{/each}
		</div>
	</div>
</section>

<!-- PRICING -->
<section id="tarifas" class="bg-zp-paper2 py-20 md:py-[120px]">
	<div class="mx-auto max-w-[1240px] px-5 md:px-10">
		<div class="mb-16 max-w-[780px]">
			<span
				class="mb-5 block text-[0.72rem] font-semibold tracking-[0.22em] text-zp-muted uppercase"
				>{m.landing_pricing_eyebrow()}</span
			>
			<h2 class="text-[clamp(2rem,3.8vw,3.3rem)] leading-[1.06]">
				{m.landing_pricing_h2()}
			</h2>
			<p class="mt-5 max-w-[56ch] text-[1.15rem] text-zp-muted">
				{m.landing_pricing_intro()}
			</p>
		</div>

		<!-- Block 1 -->
		<div class="mt-2">
			<div
				class="mb-6 flex items-baseline gap-4 border-b border-zp-line pb-[18px] max-md:flex-col max-md:gap-2"
			>
				<span class="font-serif text-[1.15rem] text-zp-accent">01</span>
				<h3 class="text-[1.55rem] tracking-tight">{m.landing_price_block1_h3()}</h3>
			</div>
			<div class="grid gap-6 lg:grid-cols-[0.92fr_1.08fr]">
				<div class="flex flex-col rounded-[14px] border border-zp-line bg-white p-8 md:p-10">
					<div class="text-[0.72rem] font-semibold tracking-[0.18em] text-zp-accent uppercase">
						{m.landing_price_once_label()}
					</div>
					<div class="mt-5 font-serif text-[3.4rem] leading-none tracking-tight">100€</div>
					<div class="mt-2 text-[0.92rem] text-zp-muted">
						{m.landing_price_once_note()}
					</div>
					<div class="mt-6 border-t border-zp-line">
						<div
							class="flex items-baseline justify-between gap-4 border-b border-zp-line py-3.5 text-[0.97rem]"
						>
							<span class="font-medium text-zp-ink"
								>{m.landing_price_dgmm_title()}<small
									class="mt-0.5 block text-[0.82rem] font-normal text-zp-muted"
									>{m.landing_price_dgmm_sub()}</small
								></span
							><span class="shrink-0 font-semibold">19,03€</span>
						</div>
						<div class="flex items-baseline justify-between gap-4 py-3.5 text-[0.97rem]">
							<span class="font-medium text-zp-ink"
								>{m.landing_price_zarpar_title()}<small
									class="mt-0.5 block text-[0.82rem] font-normal text-zp-muted"
									>{m.landing_price_zarpar_sub()}</small
								></span
							><span class="shrink-0 font-semibold"
								><span class="mr-2 text-[0.86rem] font-normal text-zp-muted line-through">150€</span
								>80,97€</span
							>
						</div>
					</div>
					<div class="mt-auto pt-5 text-[0.88rem] leading-snug text-zp-muted">
						<span
							class="mr-2 inline-block rounded-[5px] bg-zp-green/16 px-2 py-1 align-middle text-[0.6rem] font-bold tracking-wider text-zp-green uppercase"
							>{m.landing_price_launch_badge()}</span
						>{m.landing_price_launch_note()}
					</div>
				</div>
				<div
					class="flex flex-col justify-center rounded-[14px] border border-zp-line bg-white p-8 md:px-9"
				>
					<div class="mb-2 text-[0.72rem] font-semibold tracking-[0.18em] text-zp-muted uppercase">
						{m.landing_price_includes_heading()}
					</div>
					<ul class="m-0 list-none p-0">
						{#each priceIncludes as item (item.title)}
							<li class="flex gap-4 border-b border-zp-line py-[18px] last:border-b-0">
								<span
									class="mt-0.5 grid size-[22px] shrink-0 place-items-center rounded-full {item.included
										? 'bg-zp-green/16 text-zp-green'
										: 'bg-zp-accent/16 text-zp-accent'}"
								>
									{#if item.included}
										<svg
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											stroke-width="3"
											class="size-3"><path d="M20 6 9 17l-5-5" /></svg
										>
									{:else}
										<svg
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											stroke-width="2.4"
											class="size-3"><path d="M12 8v8M8 12h8" /></svg
										>
									{/if}
								</span>
								<span class="text-base">
									<b class="font-semibold">{item.title}</b>
									{#if item.optional || item.optionalFree}
										<span
											class="ml-2 inline-block rounded-[5px] bg-zp-green/16 px-2 py-0.5 align-middle text-[0.58rem] font-bold tracking-wider text-zp-green uppercase"
											>{m.landing_price_optional_badge()}</span
										>
									{/if}
									<small class="mt-0.5 block text-[0.86rem] font-normal text-zp-muted"
										>{item.sub}</small
									>
								</span>
							</li>
						{/each}
					</ul>
				</div>
			</div>
		</div>

		<!-- Block 2 -->
		<div class="mt-[52px]">
			<div
				class="mb-6 flex items-baseline gap-4 border-b border-zp-line pb-[18px] max-md:flex-col max-md:gap-2"
			>
				<span class="font-serif text-[1.15rem] text-zp-accent">02</span>
				<h3 class="text-[1.55rem] tracking-tight">{m.landing_price_block2_h3()}</h3>
			</div>
			<div class="grid items-center gap-6 lg:grid-cols-[0.92fr_1.08fr]">
				<div
					class="flex flex-col rounded-[14px] border border-zp-navy bg-zp-navy p-8 text-zp-paper md:p-10"
				>
					<div
						class="text-[0.72rem] font-semibold tracking-[0.18em] text-zp-accent-light uppercase"
					>
						{m.landing_price_commission_label()}
					</div>
					<div class="mt-5 font-serif text-5xl leading-[0.92] tracking-tight md:text-[5rem]">
						15%<span class="font-sans text-2xl font-semibold text-zp-paper/72"> + IVA</span>
					</div>
					<div class="mt-3.5 text-[0.95rem] text-zp-paper/72">
						{m.landing_price_commission_note()}
					</div>
					<p class="mt-6 text-[1.04rem] leading-snug text-zp-paper/86">
						{m.landing_price_commission_body()}
					</p>
					<div class="mt-auto pt-6 text-[0.88rem] leading-snug text-zp-paper/78">
						<span class="mb-1 block font-serif text-[1.7rem] text-white"
							>{m.landing_price_no_bookings_title()}</span
						>
						{m.landing_price_no_bookings_body()}
					</div>
				</div>
				<button
					type="button"
					class="zp-sim-shot block w-full cursor-pointer overflow-hidden rounded-2xl border border-zp-line bg-white text-left shadow-[0_34px_60px_-34px_rgba(27,24,64,0.34)]"
					aria-label={m.landing_sim_aria()}
					aria-haspopup="dialog"
					onclick={() => (priceSimOpen = true)}
				>
					<enhanced:img
						src="/static/img/landing/redesign-2025/precio-simulador.png"
						alt={m.landing_sim_alt()}
						loading="lazy"
						sizes="(min-width: 1024px) 630px, min(100vw - 2.5rem, 1200px)"
						class="block h-auto w-full"
					></enhanced:img>
					<span class="sr-only">{m.landing_sim_sr()}</span>
				</button>
			</div>
		</div>
	</div>
</section>

<Dialog.Root bind:open={priceSimOpen}>
	<Dialog.Content
		class="flex max-h-[min(90dvh,calc(100%-2rem))] max-w-[min(560px,calc(100%-2rem))] flex-col gap-0 overflow-hidden rounded-2xl border border-zp-line bg-white p-0 shadow-[0_34px_60px_-34px_rgba(27,24,64,0.34)] sm:max-w-[min(560px,calc(100%-2rem))] lg:max-w-[min(820px,calc(100%-2rem))]"
	>
		<Dialog.Header class="shrink-0 gap-2.5 px-5 pt-5 pb-3 md:px-6 md:pt-6">
			<p class="text-[0.72rem] font-semibold tracking-[0.18em] text-zp-accent uppercase">
				{m.landing_dialog_eyebrow()}
			</p>
			<Dialog.Title
				class="font-serif text-[1.35rem] leading-tight tracking-tight text-zp-ink md:text-[1.5rem]"
			>
				{m.landing_dialog_title()}
			</Dialog.Title>
			<Dialog.Description class="text-[0.95rem] leading-snug text-zp-muted">
				{m.landing_dialog_desc()}
			</Dialog.Description>
		</Dialog.Header>
		<div
			class="flex min-h-0 flex-1 items-center justify-center border-t border-zp-line px-3 py-3 md:px-4 md:py-4"
		>
			<enhanced:img
				src="/static/img/landing/redesign-2025/precio-simulador.png"
				alt={m.landing_sim_alt()}
				sizes="(min-width: 1024px) min(780px, calc(100vw - 2rem)), min(520px, calc(100vw - 2rem))"
				class="max-h-full w-full object-contain"
			></enhanced:img>
		</div>
	</Dialog.Content>
</Dialog.Root>

<!-- MODALITIES -->
<section class="py-20 md:py-[120px]">
	<div class="mx-auto max-w-[1240px] px-5 md:px-10">
		<div class="mb-16 max-w-[780px]">
			<span
				class="mb-5 block text-[0.72rem] font-semibold tracking-[0.22em] text-zp-muted uppercase"
				>{m.landing_mod_eyebrow()}</span
			>
			<h2 class="text-[clamp(2rem,3.8vw,3.3rem)] leading-[1.06]">
				{m.landing_mod_h2()}
			</h2>
			<p class="mt-5 max-w-[56ch] text-[1.15rem] text-zp-muted">
				{m.landing_mod_intro()}
			</p>
		</div>
		<div class="grid gap-6 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr]">
			<a
				href={localizeHref('/alquiler-lista-septima')}
				class="zp-mod m1 relative flex min-h-[440px] flex-col justify-end overflow-hidden rounded-[18px] p-9 text-zp-paper"
			>
				<span
					class="absolute top-[30px] left-9 rounded-[5px] bg-zp-accent px-3 py-1.5 text-[0.66rem] font-semibold tracking-[0.16em] text-white uppercase"
				>
					{m.landing_mod7_badge()}</span
				>

				<h3 class="mb-3 text-[1.7rem] leading-tight text-white">
					{m.landing_mod7_title()}
				</h3>
				<p class="mb-5 text-[0.96rem] text-zp-paper/82">
					{m.landing_mod7_body()}
				</p>
				<span
					class="zp-mod-lnk inline-flex w-fit items-center gap-2 border-b border-zp-paper/35 pb-0.5 text-[0.85rem] font-semibold"
				>
					{m.landing_mod7_link()}
					<ArrowIcon />
				</span>
			</a>
			<a
				href={localizeHref('/zarpar-experiencias')}
				class="zp-mod m2 relative flex min-h-[440px] flex-col justify-end overflow-hidden rounded-[18px] p-9 text-zp-paper"
			>
				<span
					class="absolute top-[30px] left-9 rounded-[5px] bg-zp-accent px-3 py-1.5 text-[0.66rem] font-semibold tracking-[0.16em] text-white uppercase"
				>
					{m.landing_mod_exp_badge()}</span
				>

				<h3 class="mb-3 text-[1.7rem] leading-tight text-white">
					{m.landing_mod_exp_title()}
				</h3>
				<p class="mb-5 text-[0.96rem] text-zp-paper/82">
					{m.landing_mod_exp_body()}
				</p>
				<span
					class="zp-mod-lnk inline-flex w-fit items-center gap-2 border-b border-zp-paper/35 pb-0.5 text-[0.85rem] font-semibold"
				>
					{m.landing_mod_exp_link()}
					<ArrowIcon />
				</span>
			</a>
			<a
				href={localizeHref('/alquiler-lista-sexta')}
				class="zp-mod m3 relative flex min-h-[440px] flex-col justify-end overflow-hidden rounded-[18px] p-9 text-zp-paper md:col-span-2 lg:col-span-1"
			>
				<span
					class="absolute top-[30px] left-9 rounded-[5px] bg-zp-accent px-3 py-1.5 text-[0.66rem] font-semibold tracking-[0.16em] text-white uppercase"
				>
					{m.landing_mod6_badge()}</span
				>

				<h3 class="mb-3 text-[1.7rem] leading-tight text-white">
					{m.landing_mod6_title()}
				</h3>
				<p class="mb-5 text-[0.96rem] text-zp-paper/82">
					{m.landing_mod6_body()}
				</p>
				<span
					class="zp-mod-lnk inline-flex w-fit items-center gap-2 border-b border-zp-paper/35 pb-0.5 text-[0.85rem] font-semibold"
				>
					{m.landing_mod6_link()}
					<ArrowIcon />
				</span>
			</a>
		</div>
	</div>
</section>

<!-- TESTIMONIAL -->
<section class="bg-zp-navy py-20 text-zp-paper md:py-[120px]">
	<div class="mx-auto max-w-[980px] px-5 text-center md:px-10">
		<blockquote
			class="m-0 mb-8 font-serif text-[clamp(1.6rem,3.2vw,2.6rem)] leading-snug font-light"
		>
			“{m.landing_rcnv_quote_before()}<em class="text-zp-accent-light italic"
				>{m.landing_rcnv_quote_em()}</em
			>{m.landing_rcnv_quote_after()}”
		</blockquote>
		<div class="flex items-center justify-center gap-3.5">
			<img
				src="/img/landing/redesign/Jaime-rcnvigo.jpg"
				alt="Jaime García"
				loading="lazy"
				class="size-12 rounded-full object-cover"
			/>
			<div class="text-left text-[0.92rem] tracking-wide text-zp-paper/70">
				<b class="block font-semibold text-zp-paper">Jaime García</b>
				{m.landing_rcnv_role()}
			</div>
		</div>
	</div>
</section>

<!-- REVIEWS -->
<section class="bg-zp-paper2 py-20 md:py-[120px]">
	<div class="mx-auto max-w-[1240px] px-5 md:px-10">
		<div class="mb-8 max-w-[780px]">
			<span
				class="mb-5 block text-[0.72rem] font-semibold tracking-[0.22em] text-zp-muted uppercase"
				>{m.landing_reviews_eyebrow()}</span
			>
			<h2 class="text-[clamp(2rem,3.8vw,3.3rem)] leading-[1.06]">
				{m.crisp_equal_duck_approve()}
			</h2>
		</div>
		<div class="mb-10 grid gap-6 sm:grid-cols-2 lg:mx-auto lg:max-w-[1140px] lg:grid-cols-3">
			<figure
				class="overflow-hidden rounded-2xl border border-zp-line bg-white outline-1 -outline-offset-1 outline-black/5 transition-shadow hover:shadow-[0_18px_40px_-28px_rgba(27,24,64,0.35)]"
			>
				<a
					href={instagramReviews[0].href}
					target="_blank"
					rel="noopener"
					class="block"
					aria-label={instagramReviews[0].alt}
				>
					<enhanced:img
						src="/static/img/landing/instagram-review-zarpar-motora.png"
						alt={instagramReviews[0].alt}
						loading="lazy"
						sizes="(min-width: 1024px) 360px, min(50vw - 1.5rem, 360px)"
						class="block w-full"
					></enhanced:img>
				</a>
			</figure>
			<figure
				class="overflow-hidden rounded-2xl border border-zp-line bg-white outline-1 -outline-offset-1 outline-black/5 transition-shadow hover:shadow-[0_18px_40px_-28px_rgba(27,24,64,0.35)]"
			>
				<a
					href={instagramReviews[1].href}
					target="_blank"
					rel="noopener"
					class="block"
					aria-label={instagramReviews[1].alt}
				>
					<enhanced:img
						src="/static/img/landing/instagram-review-zarpar.png"
						alt={instagramReviews[1].alt}
						loading="lazy"
						sizes="(min-width: 1024px) 360px, min(50vw - 1.5rem, 360px)"
						class="block w-full"
					></enhanced:img>
				</a>
			</figure>
			<figure
				class="overflow-hidden rounded-2xl border border-zp-line bg-white outline-1 -outline-offset-1 outline-black/5 transition-shadow hover:shadow-[0_18px_40px_-28px_rgba(27,24,64,0.35)] sm:col-span-2 sm:max-w-sm sm:justify-self-center lg:col-span-1 lg:max-w-none"
			>
				<a
					href={instagramReviews[2].href}
					target="_blank"
					rel="noopener"
					class="block"
					aria-label={instagramReviews[2].alt}
				>
					<enhanced:img
						src="/static/img/landing/zarpar-review-instagram-cies.png"
						alt={instagramReviews[2].alt}
						loading="lazy"
						sizes="(min-width: 1024px) 360px, min(50vw - 1.5rem, 360px)"
						class="block w-full"
					></enhanced:img>
				</a>
			</figure>
		</div>
		<div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
			{#each landingReviews as review (review.name)}
				<figure class="flex flex-col rounded-2xl border border-zp-line bg-white px-7 py-8">
					<div class="mb-4 flex gap-1" aria-label={m.landing_stars_aria()}>
						{#each Array(5) as _, i (i)}
							<Star class="size-4 fill-zp-accent text-zp-accent" aria-hidden="true" />
						{/each}
					</div>
					<blockquote class="m-0 grow text-[1.02rem] leading-relaxed text-zp-prose">
						“{review.quote}”
					</blockquote>
					<figcaption class="mt-6 flex items-center gap-3.5 border-t border-zp-line pt-5">
						<img
							src={review.avatarUrl}
							alt={review.name}
							loading="lazy"
							class="size-11 rounded-full object-cover"
						/>
						<div class="text-[0.88rem]">
							<span class="block font-semibold text-zp-ink">{review.name}</span>
							<span class="block text-zp-muted">{review.subtitle}</span>
						</div>
					</figcaption>
				</figure>
			{/each}
		</div>
	</div>
</section>

<!-- TRUST -->
<section class="py-20 md:py-[120px]">
	<div class="mx-auto max-w-[1240px] px-5 md:px-10">
		<div class="grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-10">
			{#each trustItems as item (item.n)}
				<div>
					<div class="mb-5 h-0.5 w-[34px] bg-zp-accent"></div>
					<div class="font-serif text-[2.6rem] leading-none text-zp-navy">{item.n}</div>
					<p class="mt-3 text-[0.92rem] text-zp-muted">{item.p}</p>
				</div>
			{/each}
		</div>
	</div>
</section>

<!-- CTA BAND -->
<section
	class="zp-cta-band"
	style="background-image: url('/img/landing/redesign-2025/photos/sunset-cockpit.avif')"
>
	<div class="relative z-2 mx-auto max-w-[1240px] px-5 py-24 text-zp-paper md:px-10 md:py-[130px]">
		<h2 class="mb-6 max-w-[16ch] text-[clamp(2.2rem,4.4vw,3.8rem)] leading-[1.05] text-white">
			{m.landing_cta_h2()}
		</h2>
		<p class="mb-9 max-w-[46ch] text-[1.15rem] text-zp-paper/85">
			{m.landing_cta_p()}
		</p>
		<a
			href={localizeHref('/anadir-barco')}
			class="inline-flex items-center gap-2 rounded-md border border-transparent bg-zp-paper px-6 py-3.5 text-[0.86rem] font-semibold text-zp-navy transition-all hover:-translate-y-px hover:bg-white"
		>
			{m.landing_cta_register()}
			<ArrowIcon />
		</a>
	</div>
</section>
